type AccountProviderSource = AccountProvider | { account_provider?: unknown } | null | undefined;

type AccountCreateCancelReference = {
  pkid: number;
  invoice_id?: string | number;
};

export function normalizeAccountProvider(value: unknown): AccountProvider {
  return value === 'HYOSUNG' || value === 'TOSS' ? value : null;
}

export function getAccountProvider(source: AccountProviderSource): AccountProvider {
  if (source && typeof source === 'object') {
    return normalizeAccountProvider(source.account_provider);
  }

  return normalizeAccountProvider(source);
}

export function getAccountCashReceiptProvider(
  source: AccountProviderSource,
  invoiceProvider: unknown,
): AccountProvider {
  return getAccountHistoryProvider(source, invoiceProvider);
}

export function getAccountHistoryProvider(source: AccountProviderSource, invoiceProvider: unknown): AccountProvider {
  return normalizeAccountProvider(invoiceProvider) ?? getAccountProvider(source);
}

export function isAccountUnused(source: AccountProviderSource): boolean {
  return getAccountProvider(source) === null;
}

export function isHyosungAccount(source: AccountProviderSource): boolean {
  return getAccountProvider(source) === 'HYOSUNG';
}

export function isTossAccount(source: AccountProviderSource): boolean {
  return getAccountProvider(source) === 'TOSS';
}

// 세부 간편결제수단이 있으면 우선 표시하고, 없는 Toss 응답은 분류값을 그대로 표시한다.
// 기존 효성 응답은 세부값이 없을 때만 기존 화면 표기와의 호환을 위해 카카오페이로 표시한다.
export function getAccountPaymentKindDisplay(
  paymentKind: string | null | undefined,
  source: AccountProviderSource,
  settleSubType?: string | null,
): string | null | undefined {
  if (paymentKind === '간편결제' && settleSubType?.trim()) {
    return settleSubType.trim();
  }

  return paymentKind === '간편결제' && !isTossAccount(source) ? '카카오페이' : paymentKind;
}

function getAccountApiBasePath(cid: string | number, executor: string | number, source: AccountProviderSource): string {
  const accountPath = isTossAccount(source) ? 'toss/account' : 'account';

  return `/${cid}/user/${executor}/${accountPath}`;
}

function getAccountInvoiceIdentifier(
  invoiceId: string | number | null | undefined,
  historyId: string | number | null | undefined,
  source: AccountProviderSource,
): string | number | null {
  const identifier = isTossAccount(source) ? historyId : invoiceId;

  return identifier === null || identifier === undefined || identifier === '' ? null : identifier;
}

export function canUseAccountManagement(source: AccountProviderSource): boolean {
  return getAccountProvider(source) !== null;
}

export function getAccountMembersApiPath(
  cid: string | number,
  uid: string | number,
  source: AccountProviderSource,
): string {
  const accountPath = isTossAccount(source) ? 'toss/account' : 'account';

  return `/${cid}/user/${uid}/${accountPath}/members`;
}

export function getAccountPendingHistoriesApiPath(
  cid: string | number,
  executor: string | number,
  uid: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountMembersApiPath(cid, executor, source)}/${uid}/history?status=1`;
}

// 청구내역 목록 조회 시 provider에 따라 Toss 전용 경로와 기존 경로를 구분한다.
export function getAccountDetailsApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  const accountPath = isTossAccount(source) ? 'toss/account' : 'account';

  return `/${cid}/user/${executor}/${accountPath}/details`;
}

export function getAccountDetailsCreateApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/details`;
}

export function getAccountCreateCancelReference(
  historyId: string | number | null | undefined,
  invoiceId: string | number | null | undefined,
  source: AccountProviderSource,
): AccountCreateCancelReference | null {
  const normalizedHistoryId = Number(historyId);

  if (!Number.isSafeInteger(normalizedHistoryId) || normalizedHistoryId <= 0) {
    return null;
  }

  if (isTossAccount(source)) {
    return { pkid: normalizedHistoryId };
  }

  if (invoiceId === null || invoiceId === undefined || invoiceId === '') {
    return null;
  }

  return { pkid: normalizedHistoryId, invoice_id: invoiceId };
}

export function getAccountMemberAttendanceHistoryApiPath(
  cid: string | number,
  executor: string | number,
  member: string | number,
  type: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountMembersApiPath(cid, executor, source)}/${member}/history/attendance?type=${type}`;
}

export function getAccountMemberCheckApiPath(
  cid: string | number,
  executor: string | number,
  uid: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountMembersApiPath(cid, executor, source)}/${uid}/check-account`;
}

export function getAccountMemberDetailsApiPath(
  cid: string | number,
  executor: string | number,
  uid: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountMembersApiPath(cid, executor, source)}/${uid}/details?type=stay`;
}

// 회원 결제이력 모달이 사용하는 정렬 조건을 유지하면서 provider별 상세 조회 경로를 만든다.
export function getAccountMemberPaymentHistoryApiPath(
  cid: string | number,
  executor: string | number,
  uid: string | number,
  sort: string,
  sortValue: string,
  source: AccountProviderSource,
): string {
  return `${getAccountMembersApiPath(cid, executor, source)}/${uid}/details?sort=${encodeURIComponent(
    sort,
  )}&sortval=${encodeURIComponent(sortValue)}`;
}

export function getAccountMemberUpdateApiPath(
  cid: string | number,
  executor: string | number,
  uid: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountMembersApiPath(cid, executor, source)}/${uid}/update`;
}

export function getAccountMembersMultiUpdateApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountMembersApiPath(cid, executor, source)}/multi/update`;
}

export function getAccountMemberPresendAttendanceApiPath(
  cid: string | number,
  executor: string | number,
  uid: string | number,
  pkid: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountMembersApiPath(cid, executor, source)}/${uid}/presend-attendance?pkid=${pkid}`;
}

export function getAccountDetailPresendAttendanceApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  const accountPath = isTossAccount(source) ? 'toss/account' : 'account';

  return `/${cid}/user/${executor}/${accountPath}/detail/presend-attendance`;
}

export function getAccountDetailsTestSendApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  const accountPath = isTossAccount(source) ? 'toss/account' : 'account';

  return `/${cid}/user/${executor}/${accountPath}/details/testSend`;
}

export function getAccountSendPretestApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  const accountPath = isTossAccount(source) ? 'toss/account' : 'account';

  return `/${cid}/user/${executor}/${accountPath}/send/pretest`;
}

export function getAccountDetailsUpdateApiPath(
  cid: string | number,
  executor: string | number,
  pkid: string | number,
  source: AccountProviderSource,
): string {
  const accountPath = isTossAccount(source) ? 'toss/account' : 'account';

  return `/${cid}/user/${executor}/${accountPath}/details/${pkid}/update`;
}

// 선택한 청구 상세의 내용을 일괄 수정하는 provider별 경로를 만든다.
export function getAccountDetailsContentUpdateApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  const accountPath = isTossAccount(source) ? 'toss/account' : 'account';

  return `/${cid}/user/${executor}/${accountPath}/details/content/update`;
}

export function getAccountManualHistoryMemoApiPath(
  cid: string | number,
  executor: string | number,
  historyId: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/history/manual/${historyId}/memo`;
}

export function getAccountHistoryListApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/history`;
}

export function getAccountCalculateApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/calculate`;
}

export function getAccountCalculateDetailApiPath(
  cid: string | number,
  executor: string | number,
  invoiceId: string | number | null | undefined,
  historyId: string | number | null | undefined,
  source: AccountProviderSource,
): string | null {
  const identifier = getAccountInvoiceIdentifier(invoiceId, historyId, source);
  return identifier === null ? null : `${getAccountCalculateApiPath(cid, executor, source)}/${identifier}`;
}

export function getAccountHistoryStatisticsApiPath(
  cid: string | number,
  executor: string | number,
  year: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountHistoryListApiPath(cid, executor, source)}/${year}/statistics`;
}

export function getAccountManualHistoryCreateApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountHistoryListApiPath(cid, executor, source)}/manual`;
}

export function getAccountManualHistoryUpdateApiPath(
  cid: string | number,
  executor: string | number,
  historyId: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountManualHistoryCreateApiPath(cid, executor, source)}/${historyId}/update`;
}

export function getAccountManualHistoryDeleteApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountManualHistoryCreateApiPath(cid, executor, source)}/delete`;
}

export function getAccountResendUpdateApiPath(
  cid: string | number,
  executor: string | number,
  status: string,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/detail/resend-updateaccount/${status}`;
}

export function getAccountDetailsSendBookmarkApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/details/send-bookmark`;
}

export function getAlimResendApiPath(
  cid: string | number,
  executor: string | number,
  type: number | null | undefined,
  source: AccountProviderSource,
): string {
  if (type !== null && type !== undefined && [21, 33, 34].includes(Number(type))) {
    return `${getAccountApiBasePath(cid, executor, source)}/resend/alim`;
  }

  return `/${cid}/user/${executor}/sms/resend/alim`;
}

export function getAccountSendOverAlimApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/send/over-alim`;
}

export function getAccountDetailsDeleteApiPath(
  cid: string | number,
  executor: string | number,
  pkid: string | number,
  source: AccountProviderSource,
): string {
  const baseUrl = `${getAccountApiBasePath(cid, executor, source)}/details`;
  return isTossAccount(source) ? baseUrl + '/delete' : baseUrl + `/${pkid}/delete`;
}

export function getAccountDetailsDeleteMultiApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  const baseUrl = `${getAccountApiBasePath(cid, executor, source)}/details`;
  return isTossAccount(source) ? baseUrl + '/delete' : baseUrl + '/delete/multi';
}

export function getAccountHistoryCancelApiPath(
  cid: string | number,
  executor: string | number,
  historyId: string | number,
  source: AccountProviderSource,
): string {
  const baseUrl = `${getAccountApiBasePath(cid, executor, source)}/history`;
  return isTossAccount(source) ? `${baseUrl}/cancel` : `${baseUrl}/${historyId}/cancel`;
}

export function getAccountHistoryCancelMultiApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  const baseUrl = `${getAccountApiBasePath(cid, executor, source)}/history`;
  return isTossAccount(source) ? `${baseUrl}/cancel` : `${baseUrl}/cancel/multi`;
}

export function getAccountDetailsHardDeleteApiPath(
  cid: string | number,
  executor: string | number,
  pkid: string | number,
  source: AccountProviderSource,
): string {
  const baseUrl = `${getAccountApiBasePath(cid, executor, source)}/details`;
  return isTossAccount(source) ? `${baseUrl}/hard-delete` : `${baseUrl}/${pkid}/hard-delete`;
}

export function getAccountDetailsHardDeleteMultiApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  const baseUrl = `${getAccountApiBasePath(cid, executor, source)}/details`;
  return isTossAccount(source) ? `${baseUrl}/hard-delete` : `${baseUrl}/hard-delete/multi`;
}

export function getAccountHistoryHardDeleteApiPath(
  cid: string | number,
  executor: string | number,
  historyId: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/history/${historyId}/hard-delete`;
}

export function getAccountHistoryRefundApiPath(
  cid: string | number,
  executor: string | number,
  historyId: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/history/${historyId}/refund`;
}

export function getAccountInvoiceDetailApiPath(
  cid: string | number,
  executor: string | number,
  invoiceId: string | number | null | undefined,
  historyId: string | number | null | undefined,
  source: AccountProviderSource,
): string | null {
  const identifier = getAccountInvoiceIdentifier(invoiceId, historyId, source);
  const isToss = isTossAccount(source);
  const baseUrl = `/${cid}/user/${executor}/`;

  return baseUrl + (isToss ? `toss/account/invoice/${identifier}/detail` : `account/invoiceDetail/${identifier}`);

  // return identifier === null ? null : `${getAccountApiBasePath(cid, executor, source)}/invoiceDetail/${identifier}`;
}

export function getAccountInvoiceApiPath(
  cid: string | number,
  executor: string | number,
  invoiceId: string | number | null | undefined,
  historyId: string | number | null | undefined,
  source: AccountProviderSource,
): string | null {
  const identifier = getAccountInvoiceIdentifier(invoiceId, historyId, source);

  return identifier === null ? null : `${getAccountApiBasePath(cid, executor, source)}/invoice/${identifier}`;
}

export function getAccountCashReceiptStatusApiPath(
  cid: string | number,
  executor: string | number,
  invoiceId: string | number | null | undefined,
  historyId: string | number | null | undefined,
  source: AccountProviderSource,
): string | null {
  const identifier = getAccountInvoiceIdentifier(invoiceId, historyId, source);

  return identifier === null ? null : `${getAccountApiBasePath(cid, executor, source)}/invoice/${identifier}/receipt`;
}

export function getAccountCashReceiptIssueApiPath(
  cid: string | number,
  executor: string | number,
  historyId: string | number | null | undefined,
  source: AccountProviderSource,
): string | null {
  if (historyId === null || historyId === undefined || historyId === '') {
    return null;
  }

  return `${getAccountApiBasePath(cid, executor, source)}/history/${historyId}/receipt`;
}

export function getAccountCashReceiptCancelApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/history/invoice/receipt/cancel`;
}

export function getAccountReceiptPrintApiPath(
  cid: string | number,
  executor: string | number,
  historyId: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountApiBasePath(cid, executor, source)}/reciept/${historyId}`;
}

export function getAccountMembersDeleteApiPath(
  cid: string | number,
  executor: string | number,
  source: AccountProviderSource,
): string {
  return `${getAccountMembersApiPath(cid, executor, source)}/delete`;
}
